import { useEffect } from 'react';
import { QueryKey, useQuery } from '@tanstack/react-query';
import { getEvent } from '@hqo/service-booking-client';
import { EventRO } from '@hqo/service-booking-types';

import { ERROR_DEFAULT, ERROR_EVENT_NOT_FOUND } from 'app/shared/consts/error-codes';
import { useApi } from 'app/shared/contexts/api';
import { useErrors } from 'app/shared/contexts/errors';
import { useServiceBookingAuth } from 'app/shared/hooks/use-service-booking-auth';
import { useUser } from 'app/shared/contexts/user';
import { retryOnAuthError } from 'app/shared/auth';

export const EVENT_QUERY_KEY = 'event';

/**
 * Cache key includes the user uuid so an auth swap doesn't serve a
 * different user's audience-scoped event response from the cache.
 * Events are audience-gated reads on the BE — same threat model as
 * bookings.
 */
export const getUseEventQueryKey = (
  eventUuid: string | undefined,
  userUuid: string | undefined,
): QueryKey => [EVENT_QUERY_KEY, userUuid ?? '', eventUuid ?? ''];

/** Thrown by `useEventQuery`'s queryFn when the requested event uuid resolves to a 404. */
export class EventNotFoundError extends Error {
  constructor(eventUuid: string) {
    super(`Event not found: ${eventUuid}`);
    this.name = 'EventNotFoundError';
  }
}

export interface UseEventQueryResponse {
  event: EventRO | null;
  error: Error | null;
  isLoading: boolean;
  isSuccess: boolean;
}

/**
 * Loads the event (and its content-service block) in one round-trip via
 * `@hqo/service-booking-client`'s `getEvent`. Opts into `includeContent` so
 * the page has access to the content-service description / image / host
 * copy without a second fetch.
 *
 * Failures flip the page into terminal-error mode via the `genericErrors`
 * channel — the event is required for the page to render anything useful.
 * A 404 maps to `ERROR_EVENT_NOT_FOUND` so `/error` shows the dedicated
 * copy; anything else falls back to the generic "Something went wrong".
 */
export const useEventQuery = (eventUuid: string | undefined): UseEventQueryResponse => {
  const { apiUrl } = useApi();
  const getAuthOptions = useServiceBookingAuth();
  const { addGenericError, hideGenericErrors } = useErrors();
  const { user } = useUser();

  const { data, error, isLoading, isSuccess } = useQuery<EventRO | null, Error>({
    queryKey: getUseEventQueryKey(eventUuid, user?.uuid),
    queryFn: async () => {
      const auth = await getAuthOptions();
      const result = await getEvent(
        { baseUrl: apiUrl, eventUuid: eventUuid ?? '', includeContent: true },
        auth,
      );
      if (!result.isSuccess()) {
        // The client lib surfaces upstream HTTP failures as a plain `Error`
        // whose message embeds the response status. Match a standalone 404
        // or any "not found" wording so /error renders the typed
        // "event not found" copy; everything else stays generic.
        //
        // TODO: switch to `result.errorData?.statusCode === 404` once
        // `@hqo/service-booking-client` surfaces a typed `statusCode`
        // on `errorData`. Until then this regex is the only signal —
        // err on the side of broader matching since a false positive
        // here just shows the "not found" copy instead of the generic
        // error one.
        const msg = result.errorData?.message ?? '';
        if (/\b404\b/.test(msg) || /not[ _-]?found/i.test(msg)) {
          throw new EventNotFoundError(eventUuid ?? '');
        }
        throw result.errorData;
      }
      return result.value.data;
    },
    enabled: !!eventUuid && !!apiUrl && !!user?.uuid,
    retry: retryOnAuthError,
  });

  useEffect(() => {
    if (error) {
      const errorCode = error instanceof EventNotFoundError ? ERROR_EVENT_NOT_FOUND : ERROR_DEFAULT;
      addGenericError(EVENT_QUERY_KEY, { error, errorCode });
    } else {
      hideGenericErrors(EVENT_QUERY_KEY);
    }
  }, [error, addGenericError, hideGenericErrors]);

  return {
    event: data ?? null,
    error: error ?? null,
    isLoading,
    isSuccess,
  };
};
